import React from 'react';
import { Card, CardContent, Box, Typography, Grid, IconButton } from '@mui/material';
import { Check, Close, Edit } from '@mui/icons-material';

/**
 * Amenity configuration - maps facility boolean fields to display labels
 */
const AMENITIES = [
  { key: 'is_parking_available', label: 'Parking Available' },
  { key: 'is_restrooms_available', label: 'Locker Rooms Available' },
  { key: 'is_wifi_available', label: 'WiFi Available' },
  { key: 'is_food_available', label: 'Food Available' },
  { key: 'is_shower_available', label: 'Shower Available' },
  { key: 'is_lounge_available', label: 'Lounge Available' },
];

/**
 * FacilityBooleanItem - Single amenity row with icon and label
 */
const FacilityBooleanItem = ({ value, label }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}
  >
    {value ? (
      <Check sx={{ color: '#4caf50', fontSize: 20 }} />
    ) : (
      <Close sx={{ color: '#f44336', fontSize: 20 }} />
    )}
    <Typography
      variant="body2"
      sx={{
        color: '#000000',
        fontSize: '0.875rem',
      }}
    >
      {label}
    </Typography>
  </Box>
);

/**
 * FacilityBoolean - Displays facility amenities as boolean indicators in a card
 * 
 * @param {object} facility - Facility object with boolean amenity fields
 * @param {string} title - Card title (default: 'Amenities')
 * @param {function} onEdit - Callback when edit button is clicked
 * @param {number} columns - Number of columns for grid layout (default: 2)
 * @param {number} spacing - Spacing between items (default: 2)
 * @param {number} elevation - Card elevation (default: 1)
 * @param {object} sx - Additional styles for the card
 */
const FacilityBoolean = ({
  facility,
  title = 'Amenities',
  onEdit,
  columns = 2,
  spacing = 2,
  elevation = 1,
  sx = {},
}) => {
  if (!facility) {
    return (
      <Card
        elevation={elevation}
        sx={{
          height: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          ...sx,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No facility information available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={elevation}
      sx={{
        height: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        ...sx,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header with title and edit button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {onEdit && (
            <IconButton
              size="small"
              onClick={onEdit}
              sx={{ color: '#666666', ml: 1 }}
              title="Edit Amenities"
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Amenities Grid */}
        <Grid container spacing={spacing}>
          {AMENITIES.map((amenity) => (
            <Grid item xs={12 / columns} key={amenity.key}>
              <FacilityBooleanItem
                value={facility[amenity.key]}
                label={amenity.label}
              />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default FacilityBoolean;
